import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KMinBanner, KMinGuard, StatusBadge } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ShieldAlert } from "lucide-react";

const RISK_COLORS: Record<string, string> = {
  "Trivial": "#22c55e",
  "Tolerável": "#84cc16",
  "Moderado": "#eab308",
  "Substancial": "#f97316",
  "Intolerável": "#ef4444",
};

export default function RHRisco() {
  usePageTitle("Risco RH");
  const { user } = useAuth();
  const { data, isLoading, error } = trpc.dashboards.rh.risk.useQuery(undefined, {
    enabled: !!user && user.role === "client_rh",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Mapa de Risco">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title="Dashboard — Mapa de Risco">
      <div className="space-y-6">
        {data?.kMin && <KMinBanner kMin={data.kMin} />}

        {/* Risk Distribution Summary */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4">Distribuição de Risco por Classificação</h3>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {(data?.riskDistribution ?? []).map((r) => (
                <div key={r.classification} className="text-center p-3 rounded-lg" style={{ backgroundColor: `${RISK_COLORS[r.classification] || "#888"}15` }}>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: RISK_COLORS[r.classification] || "#888" }}>
                    {r.count}
                  </div>
                  <div className="text-xs font-medium mt-1" style={{ color: RISK_COLORS[r.classification] || "#888" }}>
                    {r.classification}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {r.percentage}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Risk by Sector */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4">Risco por Setor</h3>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data?.bySector ?? []).length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum resultado disponível</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Setor</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Avaliações</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Risco Predominante</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Respondentes</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">kMin</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.bySector.map((s, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{s.sector}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{s.evaluationCount}</td>
                      <td className="px-3 py-2.5">
                        <KMinGuard meetsKMin={s.meetsKMin} kMin={data!.kMin} respondentCount={s.respondentCount}>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RISK_COLORS[s.predominantRisk] || "#888" }} />
                            <span className="font-medium">{s.predominantRisk}</span>
                          </span>
                        </KMinGuard>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{s.respondentCount}</td>
                      <td className="px-3 py-2.5">
                        {s.meetsKMin ? (
                          <span className="text-emerald-600 text-xs font-medium">OK</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                            <ShieldAlert className="h-3 w-3" /> Protegido
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
